import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Badge,
  Alert,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Snackbar,
  RadioGroup,
  Radio
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Block as BlockIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/pt-br';
import { usersManagementStore, servicesStore, employeesManagementStore } from '../stores/AdminStore';

moment.locale('pt-br');

const UserManagement = observer(() => {
  const [tabValue, setTabValue] = useState('pending');
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvalRole, setApprovalRole] = useState('employee');
  const [selectedServices, setSelectedServices] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [employeeOption, setEmployeeOption] = useState('create'); // 'create' or 'link'
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  useEffect(() => {
    // Check if user is admin
    const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
    if (user.role !== 'admin') {
      window.location.href = '/';
      return;
    }

    // Load services and employees for employee assignment
    servicesStore.loadServices();
    employeesManagementStore.init();

    // Load users initially
    loadUsersForTab('pending');
    // eslint-disable-next-line
  }, []);

  const loadUsersForTab = (tab) => {
    const filters = {};
    if (tab === 'pending') filters.status = 'pending';
    if (tab === 'active') filters.status = 'active';
    usersManagementStore.loadUsers(filters);
  };

  useEffect(() => {
    loadUsersForTab(tabValue);
    // eslint-disable-next-line
  }, [tabValue]);

  useEffect(() => {
    if (usersManagementStore.successMessage) {
      setSnackbarOpen(true);
    }
    // usersManagementStore.successMessage is a MobX observable; observer() handles reactivity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersManagementStore.successMessage]);

  const handleApprove = (user) => {
    setSelectedUser(user);
    setApprovalRole('employee');
    setSelectedServices([]);
    setEmployeeOption('create');
    setSelectedEmployeeId('');
    setApprovalDialog(true);
  };

  const confirmApproval = async () => {
    // If role is employee, we need to either create a new employee or link to existing
    if (approvalRole === 'employee') {
      if (employeeOption === 'create') {
        // First create the employee
        const employeeData = {
          name: selectedUser.name,
          services: selectedServices,
          active: true,
          timezone: 'America/Sao_Paulo'
        };

        try {
          const newEmployee = await employeesManagementStore.createEmployee(employeeData);
          // Then link the user to this employee
          await employeesManagementStore.linkUserToEmployee(newEmployee._id, selectedUser.id);
        } catch (err) {
          console.error('Failed to create employee:', err);
          return;
        }
      } else if (employeeOption === 'link' && selectedEmployeeId) {
        // Link the user to the selected employee
        try {
          await employeesManagementStore.linkUserToEmployee(selectedEmployeeId, selectedUser.id);
        } catch (err) {
          console.error('Failed to link employee:', err);
          return;
        }
      }
    }

    // Now approve the user with the appropriate role
    const success = await usersManagementStore.approveUser(selectedUser.id, approvalRole, null);
    if (success) {
      setApprovalDialog(false);
      setSelectedUser(null);
      setEmployeeOption('create');
      setSelectedEmployeeId('');
    }
  };

  const handleReject = async (userId) => {
    if (window.confirm('Rejeitar este usuário? Isso removerá a conta permanentemente.')) {
      await usersManagementStore.rejectUser(userId);
    }
  };

  const handleDeactivate = async (userId) => {
    if (window.confirm('Desativar este usuário? O acesso será removido.')) {
      await usersManagementStore.deactivateUser(userId);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'employee':
        return 'Barbeiro';
      case 'customer':
        return 'Cliente';
      default:
        return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'employee':
        return 'warning';
      case 'customer':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nome',
      width: 200
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250
    },
    {
      field: 'requestedAt',
      headerName: 'Solicitado',
      width: 150,
      valueFormatter: (params) => params ? moment(params).fromNow() : '-'
    },
    {
      field: 'role',
      headerName: 'Função',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          color={getRoleColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        if (!params.row.approved) {
          return <Chip label="Pendente" color="warning" size="small" />;
        }
        if (params.row.deactivatedAt) {
          return <Chip label="Desativado" color="error" size="small" />;
        }
        return <Chip label="Ativo" color="success" size="small" />;
      }
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 250,
      renderCell: (params) => {
        if (!params.row.approved) {
          return (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleApprove(params.row)}
                sx={{ mr: 1 }}
              >
                Aprovar
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleReject(params.row.id)}
              >
                Rejeitar
              </Button>
            </>
          );
        }
        return (
          <>
            <IconButton size="small" disabled title="Editar (em breve)">
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeactivate(params.row.id)}>
              <BlockIcon />
            </IconButton>
          </>
        );
      }
    }
  ];

  const filteredUsers = usersManagementStore.users.filter(user => {
    if (tabValue === 'pending') return !user.approved;
    if (tabValue === 'active') return user.approved && !user.deactivatedAt;
    return true;
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Gerenciamento de Usuários
      </Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab
          label={
            <Badge badgeContent={usersManagementStore.pendingUsersCount} color="error">
              Pendentes
            </Badge>
          }
          value="pending"
        />
        <Tab label="Ativos" value="active" />
        <Tab label="Todos" value="all" />
      </Tabs>

      {tabValue === 'pending' && filteredUsers.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Estes usuários tentaram fazer login no painel admin e estão aguardando aprovação.
        </Alert>
      )}

      {tabValue === 'pending' && filteredUsers.length === 0 && !usersManagementStore.isLoading && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Não há usuários pendentes de aprovação.
        </Alert>
      )}

      {usersManagementStore.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => usersManagementStore.setError(null)}>
          {usersManagementStore.error}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={usersManagementStore.isLoading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aprovar Usuário</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Aprovando: <strong>{selectedUser?.name}</strong> ({selectedUser?.email})
          </Alert>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Função</InputLabel>
            <Select
              value={approvalRole}
              onChange={(e) => setApprovalRole(e.target.value)}
              label="Função"
            >
              <MenuItem value="employee">Barbeiro</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </Select>
          </FormControl>

          {approvalRole === 'employee' && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Opção de Funcionário:
              </Typography>
              <RadioGroup
                value={employeeOption}
                onChange={(e) => setEmployeeOption(e.target.value)}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="create" control={<Radio />} label="Criar novo funcionário" />
                <FormControlLabel value="link" control={<Radio />} label="Vincular a funcionário existente" />
              </RadioGroup>

              {employeeOption === 'create' && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Serviços que pode realizar:
                  </Typography>
                  <FormGroup>
                    {servicesStore.services.map(service => (
                      <FormControlLabel
                        key={service.id}
                        control={
                          <Checkbox
                            checked={selectedServices.includes(service.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedServices([...selectedServices, service.id]);
                              } else {
                                setSelectedServices(selectedServices.filter(id => id !== service.id));
                              }
                            }}
                          />
                        }
                        label={`${service.name} (${service.durationMinutes} min)`}
                      />
                    ))}
                  </FormGroup>
                </>
              )}

              {employeeOption === 'link' && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Funcionário</InputLabel>
                  <Select
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    label="Funcionário"
                  >
                    {employeesManagementStore.employees
                      .filter(emp => emp.active && !emp.userId)
                      .map(employee => (
                        <MenuItem key={employee._id} value={employee._id}>
                          {employee.name} {employee.nickname && `(${employee.nickname})`}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancelar</Button>
          <Button onClick={confirmApproval} variant="contained" color="success">
            Aprovar e Ativar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbarOpen(false);
          usersManagementStore.setSuccess(null);
        }}
        message={usersManagementStore.successMessage}
      />
    </Box>
  );
});

export default UserManagement;
